import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, ButtonProps, Divider, IconButton, Slide, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLayoutEffect, useRef, useState } from "react";
import { palette } from "theme";

export interface SelectionActionBarAction extends ButtonProps {
  id: string;
  title: string;
  tooltip: string;
  onClick: () => void;
  loading?: boolean;
}

interface SelectionActionBarProps {
  open: boolean;
  selectionLabel: string;
  actions: SelectionActionBarAction[];
  onClose: () => void;
}

const SelectionActionBar = ({ open, selectionLabel, actions, onClose }: SelectionActionBarProps) => {
  const actionBarRef = useRef<HTMLDivElement | null>(null);
  const [left, setLeft] = useState(16);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updateLeft = () => {
      const width = actionBarRef.current?.getBoundingClientRect().width;

      if (!width) return;

      setLeft(Math.max((window.innerWidth - width) / 2, 16));
    };

    updateLeft();
    window.addEventListener("resize", updateLeft);

    return () => window.removeEventListener("resize", updateLeft);
  }, [open]);

  const verticalDivider = (
    <Divider
      orientation='vertical'
      sx={{ borderRightWidth: 2, height: 20, borderColor: palette.base[500], display: { xs: "none", lg: "block" } }}
    />
  );

  return (
    <Slide
      direction='up'
      in={open}
      mountOnEnter
      unmountOnExit>
      <Box
        ref={actionBarRef}
        sx={{
          position: "fixed",
          left,
          bottom: 24,
          zIndex: (theme) => theme.zIndex.modal - 1,
          width: "calc(100% - 32px)",
          maxWidth: 1120,
        }}>
        <Stack
          direction='row'
          alignItems='flex-start'
          justifyContent='space-between'
          sx={{
            px: 2.5,
            py: 1.5,
            borderRadius: { xs: 4, lg: 999 },
            backgroundColor: alpha(palette.blue[200], 0.76),
            backdropFilter: "blur(10px)",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.25)",
          }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            alignItems={{ xs: "stretch", lg: "center" }}
            justifyContent='space-between'
            spacing={{ xs: 2, lg: 2 }}
            sx={{ width: "100%" }}>
            <Stack direction='row'>
              <Typography
                sx={{
                  color: "common.white",
                  fontWeight: 500,
                  pr: 1,
                }}
                variant='body14rg'>
                {selectionLabel}
              </Typography>
              {selectionLabel && verticalDivider}
            </Stack>
            <Stack
              direction={{ xs: "row", md: "row" }}
              spacing={1}
              useFlexGap
              alignItems={{ xs: "center", md: "center" }}
              flexWrap='wrap'>
              {actions.map(({ id, title, tooltip, loading, ...buttonProps }) => (
                <Tooltip
                  key={id}
                  title={tooltip}>
                  <span>
                    {loading !== undefined ? (
                      <LoadingButton
                        variant='circleContained'
                        loading={loading}
                        sx={{ color: "common.white" }}
                        {...buttonProps}>
                        {title}
                      </LoadingButton>
                    ) : (
                      <Button
                        variant='circleContained'
                        sx={{ color: "common.white" }}
                        {...buttonProps}>
                        {title}
                      </Button>
                    )}
                  </span>
                </Tooltip>
              ))}
              {actions.length > 0 && verticalDivider}
            </Stack>
          </Stack>
          <IconButton
            onClick={onClose}
            sx={{ color: "common.white" }}>
            <Close />
          </IconButton>
        </Stack>
      </Box>
    </Slide>
  );
};

export default SelectionActionBar;
